import { CreateTVShowRequest, CreateTVShowResponse, TVShow, CreateEpisodeRequest, CreateEpisodeResponse, Episode, CreateSeasonRequest, CreateSeasonResponse, Season, BulkEpisodePreviewRequest, BulkEpisodePreviewResponse, BulkCreateEpisodesResponse, EpisodeExternalIdReference, EpisodeExternalIdsResult, LinkValidationResult, Language } from '@/types/model';
import { apiClient, QueryParams, PaginatedResponse } from './apiClient';

export type TVShowListItem = Pick<TVShow, 'id' | 'title' | 'year' | 'totalEpisodes' | 'genres' | 'views' | 'active' | 'updatedDate'>;

export interface TVShowsQueryParams extends QueryParams {
  categoryId?: string;
  genre?: string;
  year?: number;
  status?: string;
  classification?: string;
  active?: boolean;
  adultContent?: boolean;
  kidsContent?: boolean;
}

export interface EpisodesQueryParams extends QueryParams {
  tvShowId?: string;
  seasonNumber?: number;
  active?: boolean;
}

export interface SeasonsQueryParams extends QueryParams {
  tvShowId?: string;
  active?: boolean;
}

export interface PlaybackLanguageParams extends QueryParams {
  audioLanguage?: Language;
  subtitleLanguage?: Language | 'NONE';
}

export interface PlaybackUrlResponse {
  playbackUrl: string;
  audioLanguages: Language[];
  subtitleLanguages: Language[];
  singleAudioLanguage: boolean;
  singleSubtitleLanguage: boolean;
}

export const tvShowService = {
  async getTVShowList(params: TVShowsQueryParams = {}): Promise<PaginatedResponse<TVShowListItem>> {
    return apiClient.getPaginated<TVShowListItem>('/tv-shows', { ...params, _view: 'list' });
  },
  /**
   * Get TV shows with pagination, search, and filtering
   */
  async getTVShows(params: TVShowsQueryParams = {}): Promise<PaginatedResponse<TVShow>> {
    return apiClient.getPaginated<TVShow>('/tv-shows', params);
  },

  /**
   * Get a single TV show by ID
   */
  async getTVShowById(id: string): Promise<TVShow> {
    return apiClient.getById<TVShow>('/tv-shows', id);
  },

  /**
   * Create a new TV show
   */
  async createTVShow(tvShowData: CreateTVShowRequest): Promise<CreateTVShowResponse> {
    return apiClient.create<CreateTVShowRequest, CreateTVShowResponse>('/tv-shows', tvShowData);
  },

  /**
   * Update a TV show
   */
  async updateTVShow(id: string, tvShowData: Partial<CreateTVShowRequest>): Promise<CreateTVShowResponse> {
    return apiClient.update<Partial<CreateTVShowRequest>, CreateTVShowResponse>('/tv-shows', id, tvShowData);
  },

  async linkTVShowToTmdb(id: string, tmdbId: string): Promise<CreateTVShowResponse> {
    return apiClient.patch<CreateTVShowResponse>(`/tv-shows/${id}/tmdb-link`, { tmdbId });
  },

  /**
   * Delete a TV show
   */
  async deleteTVShow(id: string): Promise<{ success: boolean; message: string }> {
    return apiClient.deleteById<{ success: boolean; message: string }>('/tv-shows', id);
  },

  /**
   * Toggle TV show active status
   */
  async toggleTVShowStatus(id: string, active: boolean): Promise<CreateTVShowResponse> {
    return apiClient.toggleStatus<CreateTVShowResponse>('/tv-shows', id, active);
  },

  async promoteTVShow(id: string): Promise<CreateTVShowResponse> {
    return apiClient.post<CreateTVShowResponse>(`/tv-shows/${id}/promote`);
  },

  /**
   * Import a TV show from external source
   */
  async importTVShow(payload: { id: string; token: string }): Promise<void> {
    return apiClient.get<void>(`/import/tvshow?id=${payload.id}&token=${payload.token}`);
  },

  // Episode-related services
  /**
   * Get episodes for a TV show
   */
  async getEpisodes(params: EpisodesQueryParams = {}): Promise<PaginatedResponse<Episode>> {
    return apiClient.getPaginated<Episode>('/episodes', params);
  },

  /**
   * Get episodes by TV show ID
   */
  async getEpisodesByTVShowId(tvShowId: string, params: QueryParams = {}): Promise<PaginatedResponse<Episode>> {
    return apiClient.getPaginated<Episode>(`/tv-shows/${tvShowId}/episodes`, params);
  },

  /**
   * Get a single episode by ID
   */
  async getEpisodeById(id: string): Promise<Episode> {
    return apiClient.getById<Episode>('/episodes', id);
  },

  /**
   * Resolve a private episode link to a short-lived signed playback URL.
   */
  async getEpisodePlaybackUrl(
    episodeId: string,
    params: PlaybackLanguageParams = {},
  ): Promise<PlaybackUrlResponse> {
    return apiClient.get<PlaybackUrlResponse>(
      `/app/content/tv-shows/episodes/${episodeId}/play-url`,
      params,
    );
  },

  /**
   * Create a new episode
   */
  async createEpisode(episodeData: CreateEpisodeRequest): Promise<CreateEpisodeResponse> {
    return apiClient.create<CreateEpisodeRequest, CreateEpisodeResponse>(`/tv-shows/${episodeData.tvShowId}/episodes`, episodeData);
  },

  async previewBulkEpisodes(tvShowId: string, request: BulkEpisodePreviewRequest): Promise<BulkEpisodePreviewResponse> {
    return apiClient.post<BulkEpisodePreviewResponse>(`/tv-shows/${tvShowId}/episodes/bulk-preview`, request);
  },

  async createBulkEpisodes(tvShowId: string, episodes: CreateEpisodeRequest[]): Promise<BulkCreateEpisodesResponse> {
    return apiClient.post<BulkCreateEpisodesResponse>(`/tv-shows/${tvShowId}/episodes/bulk`, { episodes });
  },

  async validateBulkEpisodeLinks(tvShowId: string, urls: string[]): Promise<LinkValidationResult[]> {
    return apiClient.post<LinkValidationResult[]>(`/tv-shows/${tvShowId}/episodes/bulk-link-validation`, { urls });
  },

  async getBulkEpisodeExternalIds(
    tvShowId: string,
    tmdbId: string,
    episodes: EpisodeExternalIdReference[],
  ): Promise<EpisodeExternalIdsResult[]> {
    return apiClient.post<EpisodeExternalIdsResult[]>(`/tv-shows/${tvShowId}/episodes/bulk-external-ids`, {
      tmdbId,
      episodes,
    });
  },

  /**
   * Update an episode
   */
  async updateEpisode(id: string, episodeData: Partial<CreateEpisodeRequest>): Promise<CreateEpisodeResponse> {
    return apiClient.update<Partial<CreateEpisodeRequest>, CreateEpisodeResponse>(`/tv-shows/${episodeData.tvShowId}/episodes`, id, episodeData);
  },

  /**
   * Delete an episode
   */
  async deleteEpisode(tvShowId: string, id: string): Promise<{ success: boolean; message: string }> {
    return apiClient.deleteById<{ success: boolean; message: string }>(`/tv-shows/${tvShowId}/episodes`, id);
  },

  async deleteEpisodes(tvShowId: string, episodeIds: string[]): Promise<{ success: boolean; message: string; count: number }> {
    return apiClient.delete<{ success: boolean; message: string; count: number }>(
      `/tv-shows/${tvShowId}/episodes/bulk`,
      { data: { episodeIds } },
    );
  },

  /**
   * Toggle episode active status
   */
  async toggleEpisodeStatus(tvShowId: string, id: string, active: boolean): Promise<CreateEpisodeResponse> {
    return apiClient.toggleStatus<CreateEpisodeResponse>(`/tv-shows/${tvShowId}/episodes`, id, active);
  },

  // Season-related services
  /**
   * Get seasons for a TV show
   */
  async getSeasons(params: SeasonsQueryParams = {}): Promise<PaginatedResponse<Season>> {
    return apiClient.getPaginated<Season>('/seasons', params);
  },

  /**
   * Get seasons by TV show ID
   */
  async getSeasonsByTVShowId(tvShowId: string, params: QueryParams = {}): Promise<PaginatedResponse<Season>> {
    return apiClient.getPaginated<Season>(`/tv-shows/${tvShowId}/seasons`, params);
  },

  /**
   * Get a single season by ID
   */
  async getSeasonById(id: string): Promise<Season> {
    return apiClient.getById<Season>('/seasons', id);
  },

  /**
   * Create a new season
   */
  async createSeason(seasonData: CreateSeasonRequest): Promise<CreateSeasonResponse> {
    return apiClient.create<CreateSeasonRequest, CreateSeasonResponse>('/seasons', seasonData);
  },

  /**
   * Update a season
   */
  async updateSeason(id: string, seasonData: Partial<CreateSeasonRequest>): Promise<CreateSeasonResponse> {
    return apiClient.update<Partial<CreateSeasonRequest>, CreateSeasonResponse>('/seasons', id, seasonData);
  },

  /**
   * Delete a season
   */
  async deleteSeason(id: string): Promise<{ success: boolean; message: string }> {
    return apiClient.deleteById<{ success: boolean; message: string }>('/seasons', id);
  },

  /**
   * Toggle season active status
   */
  async toggleSeasonStatus(id: string, active: boolean): Promise<CreateSeasonResponse> {
    return apiClient.toggleStatus<CreateSeasonResponse>('/seasons', id, active);
  },
};
